import React, { useState, useEffect, useMemo } from 'react';
import CommonSection from '../components/Ui/CommonSection';
import Helmet from '../components/Helmet/Helmet';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import useAuth from '../custom-hooks/useAuth';
import { getUserOrders } from '../services/orderService';
import InvoiceModal from '../components/Ui/InvoiceModal';
import OrderTracker from '../components/Ui/OrderTracker';
import { getProductImg, handleImgError } from '../utils/imageHelper';
import '../styles/orders.css';

const Orders = () => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedInvoiceOrder, setSelectedInvoiceOrder] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchOrders = async () => {
      if (!currentUser) return;
      setLoading(true);
      try {
        const data = await getUserOrders(currentUser.uid);
        if (isMounted) {
          setOrders(data);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching user orders:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchOrders();

    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  // Statistics calculation
  const stats = useMemo(() => {
    const total = orders.length;
    const pending = orders.filter((o) => (o.status || '').toLowerCase() === 'pending').length;
    const delivered = orders.filter((o) => (o.status || '').toLowerCase() === 'delivered').length;
    return { total, pending, delivered };
  }, [orders]);

  // Filtered orders list
  const filteredOrders = useMemo(() => {
    if (filter === 'all') return orders;
    return orders.filter((o) => (o.status || '').toLowerCase() === filter.toLowerCase());
  }, [orders, filter]);

  const getStatusClass = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'delivered':
        return 'delivered';
      case 'shipped':
        return 'shipped';
      case 'processing':
        return 'processing';
      case 'pending':
      default:
        return 'pending';
    }
  };

  const getStatusIcon = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'delivered':
        return <i className="ri-checkbox-circle-fill"></i>;
      case 'shipped':
        return <i className="ri-truck-fill"></i>;
      case 'processing':
        return <i className="ri-time-fill"></i>;
      case 'pending':
      default:
        return <i className="ri-hourglass-2-fill"></i>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recent';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Helmet title="My Orders | L'HABILLEUR">
      <CommonSection title="My Orders" />
      <section className="orders__section">
        <Container>
          {/* Stats Overview */}
          <div className="orders__stats-grid">
            <div className="orders__stat-card">
              <div className="orders__stat-icon blue">
                <i className="ri-shopping-bag-3-line"></i>
              </div>
              <div className="orders__stat-info">
                <h3>{stats.total}</h3>
                <span>Total Orders Placed</span>
              </div>
            </div>

            <div className="orders__stat-card">
              <div className="orders__stat-icon amber">
                <i className="ri-time-line"></i>
              </div>
              <div className="orders__stat-info">
                <h3>{stats.pending}</h3>
                <span>Pending Approval</span>
              </div>
            </div>

            <div className="orders__stat-card">
              <div className="orders__stat-icon green">
                <i className="ri-check-double-line"></i>
              </div>
              <div className="orders__stat-info">
                <h3>{stats.delivered}</h3>
                <span>Delivered Parcels</span>
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="orders__filter-bar">
            <button
              type="button"
              className={`orders__filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All Orders ({orders.length})
            </button>
            <button
              type="button"
              className={`orders__filter-btn ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => setFilter('pending')}
            >
              Pending ({stats.pending})
            </button>
            <button
              type="button"
              className={`orders__filter-btn ${filter === 'processing' ? 'active' : ''}`}
              onClick={() => setFilter('processing')}
            >
              Processing
            </button>
            <button
              type="button"
              className={`orders__filter-btn ${filter === 'delivered' ? 'active' : ''}`}
              onClick={() => setFilter('delivered')}
            >
              Delivered ({stats.delivered})
            </button>
          </div>

          {/* Orders Content */}
          {loading ? (
            <div className="text-center py-5">
              <div
                className="spinner-border text-primary mb-3"
                role="status"
                style={{ width: '3rem', height: '3rem', color: 'var(--primary-color)' }}
              >
                <span className="visually-hidden">Loading orders...</span>
              </div>
              <p className="text-muted fw-semibold">Fetching your order history...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="orders__empty-state">
              <div className="orders__empty-icon">
                <i className="ri-shopping-bag-3-line"></i>
              </div>
              <h3>
                {filter === 'all'
                  ? "You haven't placed any orders yet"
                  : `No orders found with status "${filter}"`}
              </h3>
              <p>
                Browse our atelier collections of handcrafted luxury furniture and timeless designs.
              </p>
              <Link to="/shop" className="orders__browse-btn">
                <i className="ri-store-2-line"></i> Browse Atelier Catalog
              </Link>
            </div>
          ) : (
            <Row>
              <Col lg="12">
                {filteredOrders.map((order) => (
                  <div key={order.id} className="order__card">
                    {/* Card Header */}
                    <div className="order__card-header">
                      <div className="order__card-meta">
                        <span className="order__number">#{order.orderNumber}</span>
                        <span className="order__date">
                          <i className="ri-calendar-line me-1"></i>
                          {formatDate(order.createdAt)}
                        </span>
                        <span className="text-muted small">
                          ({order.items?.length || 0} product{(order.items?.length || 0) === 1 ? '' : 's'})
                        </span>
                      </div>

                      <div>
                        <span className={`order__status-badge ${getStatusClass(order.status)}`}>
                          {getStatusIcon(order.status)} {order.status || 'Pending'}
                        </span>
                      </div>
                    </div>

                    {/* Multi-Stage Visual Order Tracker */}
                    <OrderTracker
                      status={order.status}
                      trackingInfo={{
                        courierName: order.courierName,
                        trackingNumber: order.trackingNumber,
                      }}
                      createdAt={order.createdAt}
                    />

                    {/* Card Body: Items List */}
                    <div className="order__card-body">
                      <div className="order__items-grid">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="order__item-row">
                            <img
                              src={getProductImg(item)}
                              onError={(e) => handleImgError(e, item)}
                              alt={item.productName}
                              className="order__item-thumb"
                            />
                            <div className="order__item-details">
                              <h6 className="order__item-name">{item.productName}</h6>
                              <div className="order__item-pricing">
                                Qty: {item.quantity} × ${item.price}
                              </div>
                            </div>
                            <div className="order__item-subtotal">
                              ${item.totalPrice || item.price * item.quantity}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Card Footer: Shipping info & total */}
                    <div className="order__card-footer">
                      <div className="order__shipping-snippet">
                        <i className="ri-map-pin-2-line"></i>
                        <strong>Shipping to: </strong>
                        {order.customerName} ({order.phoneNumber}) — {order.shippingAddress?.address},{' '}
                        {order.shippingAddress?.city}, {order.shippingAddress?.country}
                      </div>

                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          className="order__invoice-btn"
                          onClick={() => setSelectedInvoiceOrder(order)}
                          title="Print Official Atelier Invoice"
                        >
                          <i className="ri-printer-line me-1"></i> Print Invoice
                        </button>

                        <span className="badge bg-light text-dark border px-3 py-2 small">
                          <i className="ri-money-dollar-circle-line text-success me-1"></i>
                          {order.paymentMethod || 'Cash on Delivery'}
                        </span>
                        <div className="order__total-amount">
                          Total: ${order.totalAmount}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Col>
            </Row>
          )}
        </Container>
      </section>

      {/* Official Printable Atelier Invoice Modal */}
      <InvoiceModal
        order={selectedInvoiceOrder}
        isOpen={Boolean(selectedInvoiceOrder)}
        onClose={() => setSelectedInvoiceOrder(null)}
      />
    </Helmet>
  );
};

export default Orders;
